/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Platform API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  CloseOrganizationRequest,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  DeletePlatformV1OrganizationsIdPathParameters,
  DeletePlatformV1SupportSessionsIdPathParameters,
  GetPlatformV1Organizations200,
  GetPlatformV1OrganizationsIdExportJobIdDownloadParams,
  GetPlatformV1OrganizationsIdExportJobIdDownloadPathParameters,
  GetPlatformV1OrganizationsIdExportJobIdPathParameters,
  GetPlatformV1OrganizationsIdPathParameters,
  GetPlatformV1OrganizationsIdUsagePathParameters,
  GetPlatformV1OrganizationsParams,
  GetPlatformV1SupportSessions200,
  GetPlatformV1SupportSessionsParams,
  Organization,
  OrganizationDetail,
  OrganizationExportJob,
  OrganizationUsage,
  OwnerInviteSent,
  PatchPlatformV1OrganizationsIdOwnerInvitePathParameters,
  PatchPlatformV1OrganizationsIdPathParameters,
  PostPlatformV1OrganizationsIdClosePathParameters,
  PostPlatformV1OrganizationsIdExportPathParameters,
  PostPlatformV1OrganizationsIdReactivatePathParameters,
  PostPlatformV1OrganizationsIdReopenPathParameters,
  PostPlatformV1OrganizationsIdResendOwnerInvitePathParameters,
  PostPlatformV1OrganizationsIdSupportSessionPathParameters,
  PostPlatformV1OrganizationsIdSuspendPathParameters,
  StartSupportSessionRequest,
  SupportSessionStarted,
  SuspendOrganizationRequest,
  UpdateOrganizationRequest,
  UpdateOwnerInviteRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Each row says whether an owner has accepted yet (`ownerStatus`) and how many stores are live
 * @summary List organizations, newest first
 */
export const getPlatformV1Organizations = (
    params?: GetPlatformV1OrganizationsParams,
 options?: SecondParameter<typeof customInstance<GetPlatformV1Organizations200>>,) => {
      return customInstance<GetPlatformV1Organizations200>(
      {url: `/platform/v1/organizations`, method: 'GET',
        params
    },
      options);
    }
  /**
 * With `owner.password` the owner can sign in at once. Without it a new owner is created invited and emailed a 7-day set-password link (`owner.invited: true`); an email that already has a user attaches that user as an active owner and emails them about the new organization. Emails leave from the worker, so this never fails after the organization exists. `warnings` lists what still blocks selling, such as a missing exchange rate for the store currency
 * @summary Provision an organization with its first store and owner
 */
export const postPlatformV1Organizations = (
    createOrganizationRequest: BodyType<CreateOrganizationRequest>,
 options?: SecondParameter<typeof customInstance<CreateOrganizationResponse>>,) => {
      return customInstance<CreateOrganizationResponse>(
      {url: `/platform/v1/organizations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createOrganizationRequest
    },
      options);
    }
  /**
 * @summary Get an organization with its stores, owners and staff count
 */
export const getPlatformV1OrganizationsId = (
    { id }: GetPlatformV1OrganizationsIdPathParameters,
 options?: SecondParameter<typeof customInstance<OrganizationDetail>>,) => {
      return customInstance<OrganizationDetail>(
      {url: `/platform/v1/organizations/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * The slug is immutable. Status changes go through suspend, reactivate, close and reopen
 * @summary Change an organization's name, billing email or operator notes
 */
export const patchPlatformV1OrganizationsId = (
    { id }: PatchPlatformV1OrganizationsIdPathParameters,
    updateOrganizationRequest: BodyType<UpdateOrganizationRequest>,
 options?: SecondParameter<typeof customInstance<Organization>>,) => {
      return customInstance<Organization>(
      {url: `/platform/v1/organizations/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateOrganizationRequest
    },
      options);
    }
  /**
 * Irreversible. 409 CONFLICT (meta.status) unless the organization is closed. Removes its stores, catalogue, customers, orders, media files and search indexes, and the staff accounts that belonged to no other organization; the platform audit log keeps the record
 * @summary Delete a closed organization and everything it owns
 */
export const deletePlatformV1OrganizationsId = (
    { id }: DeletePlatformV1OrganizationsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/organizations/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary What an organization holds: products, variants, orders, stores, members, keys and media bytes
 */
export const getPlatformV1OrganizationsIdUsage = (
    { id }: GetPlatformV1OrganizationsIdUsagePathParameters,
 options?: SecondParameter<typeof customInstance<OrganizationUsage>>,) => {
      return customInstance<OrganizationUsage>(
      {url: `/platform/v1/organizations/${id}/usage`, method: 'GET'
    },
      options);
    }
  /**
 * The worker issues the link within seconds; earlier links stop working then. 409 PASSWORD_ALREADY_SET when no owner of the organization is still invited
 * @summary Email the invited owner a fresh set-password link
 */
export const postPlatformV1OrganizationsIdResendOwnerInvite = (
    { id }: PostPlatformV1OrganizationsIdResendOwnerInvitePathParameters,
 options?: SecondParameter<typeof customInstance<OwnerInviteSent>>,) => {
      return customInstance<OwnerInviteSent>(
      {url: `/platform/v1/organizations/${id}/resend-owner-invite`, method: 'POST'
    },
      options);
    }
  /**
 * Only while the owner is still invited (409 PASSWORD_ALREADY_SET otherwise). The membership moves to the account for the new address (existing or new); the old address's links stop working. 409 ALREADY_EXISTS when the new address is already an active member
 * @summary Fix the invited owner's email address and resend the invitation
 */
export const patchPlatformV1OrganizationsIdOwnerInvite = (
    { id }: PatchPlatformV1OrganizationsIdOwnerInvitePathParameters,
    updateOwnerInviteRequest: BodyType<UpdateOwnerInviteRequest>,
 options?: SecondParameter<typeof customInstance<OwnerInviteSent>>,) => {
      return customInstance<OwnerInviteSent>(
      {url: `/platform/v1/organizations/${id}/owner-invite`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateOwnerInviteRequest
    },
      options);
    }
  /**
 * Live staff sessions are revoked and the owners are emailed the reason
 * @summary Suspend an organization; its storefronts and staff sessions stop at once
 */
export const postPlatformV1OrganizationsIdSuspend = (
    { id }: PostPlatformV1OrganizationsIdSuspendPathParameters,
    suspendOrganizationRequest: BodyType<SuspendOrganizationRequest>,
 options?: SecondParameter<typeof customInstance<Organization>>,) => {
      return customInstance<Organization>(
      {url: `/platform/v1/organizations/${id}/suspend`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: suspendOrganizationRequest
    },
      options);
    }
  /**
 * @summary Reactivate a suspended organization
 */
export const postPlatformV1OrganizationsIdReactivate = (
    { id }: PostPlatformV1OrganizationsIdReactivatePathParameters,
 options?: SecondParameter<typeof customInstance<Organization>>,) => {
      return customInstance<Organization>(
      {url: `/platform/v1/organizations/${id}/reactivate`, method: 'POST'
    },
      options);
    }
  /**
 * 409 OPEN_ORDERS (meta.openOrders) while orders are still open unless `force` is true. Reversible with POST .../reopen; the stores stay paused until the owner activates them again
 * @summary Close an organization: sign everyone out, stop its API keys, pause its live stores
 */
export const postPlatformV1OrganizationsIdClose = (
    { id }: PostPlatformV1OrganizationsIdClosePathParameters,
    closeOrganizationRequestNull: BodyType<CloseOrganizationRequest | null>,
 options?: SecondParameter<typeof customInstance<Organization>>,) => {
      return customInstance<Organization>(
      {url: `/platform/v1/organizations/${id}/close`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: closeOrganizationRequestNull
    },
      options);
    }
  /**
 * 409 CONFLICT unless the organization is closed. Staff can sign in again; stores stay paused until activated
 * @summary Reopen a closed organization
 */
export const postPlatformV1OrganizationsIdReopen = (
    { id }: PostPlatformV1OrganizationsIdReopenPathParameters,
 options?: SecondParameter<typeof customInstance<Organization>>,) => {
      return customInstance<Organization>(
      {url: `/platform/v1/organizations/${id}/reopen`, method: 'POST'
    },
      options);
    }
  /**
 * Products for the organization plus orders, customers and inventory per store, built on the worker. Poll GET .../export/{jobId}; the download link lasts 24 hours
 * @summary Start a full export of an organization as a zip of CSVs
 */
export const postPlatformV1OrganizationsIdExport = (
    { id }: PostPlatformV1OrganizationsIdExportPathParameters,
 options?: SecondParameter<typeof customInstance<OrganizationExportJob>>,) => {
      return customInstance<OrganizationExportJob>(
      {url: `/platform/v1/organizations/${id}/export`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Status of an organization export, with its signed download link once ready
 */
export const getPlatformV1OrganizationsIdExportJobId = (
    { id, jobId }: GetPlatformV1OrganizationsIdExportJobIdPathParameters,
 options?: SecondParameter<typeof customInstance<OrganizationExportJob>>,) => {
      return customInstance<OrganizationExportJob>(
      {url: `/platform/v1/organizations/${id}/export/${jobId}`, method: 'GET'
    },
      options);
    }
  /**
 * No token needed: `exp` and `sig` come from the downloadUrl of GET .../export/{jobId} and stop working after 24 hours (401 TOKEN_EXPIRED)
 * @summary Download an organization export with its signed link
 */
export const getPlatformV1OrganizationsIdExportJobIdDownload = (
    { id, jobId }: GetPlatformV1OrganizationsIdExportJobIdDownloadPathParameters,
    params: GetPlatformV1OrganizationsIdExportJobIdDownloadParams,
 options?: SecondParameter<typeof customInstance<unknown>>,) => {
      return customInstance<unknown>(
      {url: `/platform/v1/organizations/${id}/export/${jobId}/download`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Answers the admin login envelope (staff access token plus the nucleus_staff cookie) for a read-only principal that holds every *.read permission of the organization and nothing else; every write answers 403 SUPPORT_SESSION_READ_ONLY. Recorded in the organization's audit log and listed under GET /support-sessions. Needs two-factor on the platform account (403 TWO_FACTOR_REQUIRED)
 * @summary Open a 60-minute read-only support session inside an organization
 */
export const postPlatformV1OrganizationsIdSupportSession = (
    { id }: PostPlatformV1OrganizationsIdSupportSessionPathParameters,
    startSupportSessionRequest: BodyType<StartSupportSessionRequest>,
 options?: SecondParameter<typeof customInstance<SupportSessionStarted>>,) => {
      return customInstance<SupportSessionStarted>(
      {url: `/platform/v1/organizations/${id}/support-session`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: startSupportSessionRequest
    },
      options);
    }
  /**
 * @summary Support sessions, newest first
 */
export const getPlatformV1SupportSessions = (
    params?: GetPlatformV1SupportSessionsParams,
 options?: SecondParameter<typeof customInstance<GetPlatformV1SupportSessions200>>,) => {
      return customInstance<GetPlatformV1SupportSessions200>(
      {url: `/platform/v1/support-sessions`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Every staff session opened on it stops at once; already-ended sessions answer 204 too
 * @summary End a support session now
 */
export const deletePlatformV1SupportSessionsId = (
    { id }: DeletePlatformV1SupportSessionsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/support-sessions/${id}`, method: 'DELETE'
    },
      options);
    }
  export type GetPlatformV1OrganizationsResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1Organizations>>>
export type PostPlatformV1OrganizationsResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1Organizations>>>
export type GetPlatformV1OrganizationsIdResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1OrganizationsId>>>
export type PatchPlatformV1OrganizationsIdResult = NonNullable<Awaited<ReturnType<typeof patchPlatformV1OrganizationsId>>>
export type DeletePlatformV1OrganizationsIdResult = NonNullable<Awaited<ReturnType<typeof deletePlatformV1OrganizationsId>>>
export type GetPlatformV1OrganizationsIdUsageResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1OrganizationsIdUsage>>>
export type PostPlatformV1OrganizationsIdResendOwnerInviteResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OrganizationsIdResendOwnerInvite>>>
export type PatchPlatformV1OrganizationsIdOwnerInviteResult = NonNullable<Awaited<ReturnType<typeof patchPlatformV1OrganizationsIdOwnerInvite>>>
export type PostPlatformV1OrganizationsIdSuspendResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OrganizationsIdSuspend>>>
export type PostPlatformV1OrganizationsIdReactivateResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OrganizationsIdReactivate>>>
export type PostPlatformV1OrganizationsIdCloseResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OrganizationsIdClose>>>
export type PostPlatformV1OrganizationsIdReopenResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OrganizationsIdReopen>>>
export type PostPlatformV1OrganizationsIdExportResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OrganizationsIdExport>>>
export type GetPlatformV1OrganizationsIdExportJobIdResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1OrganizationsIdExportJobId>>>
export type GetPlatformV1OrganizationsIdExportJobIdDownloadResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1OrganizationsIdExportJobIdDownload>>>
export type PostPlatformV1OrganizationsIdSupportSessionResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OrganizationsIdSupportSession>>>
export type GetPlatformV1SupportSessionsResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1SupportSessions>>>
export type DeletePlatformV1SupportSessionsIdResult = NonNullable<Awaited<ReturnType<typeof deletePlatformV1SupportSessionsId>>>
